/**
 * Nutrazen — Fancy homepage scroll choreography.
 *
 * Lightweight (no GSAP). Uses IntersectionObserver for reveals + a single
 * rAF parallax loop. Auto-discovers any element with the relevant data-attr,
 * so any section that opts in just needs to add the class/attribute.
 *
 * Patterns surfaced via attributes:
 *   .nz-fancy-mask                -> per-line mask reveal on heading text
 *   [data-nz-fade]                -> fade-up on enter
 *   [data-nz-stagger]             -> children fade-up with stagger
 *   [data-nz-parallax="0.15"]     -> y translate linked to scroll, factor 0.15
 *   [data-nz-counter="42"]        -> count up from 0 to value when in view
 *   .nz-fancy-rule                -> hairline draws in scaleX
 */
(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ────────────────────────────────────────────────
     1) Split headings into per-line spans for the mask reveal.
     Operates on .nz-fancy-mask once — must run before observers.
     We split per line by splitting on <br> AND by detecting natural
     line wraps after layout. To keep it simple and robust we split on
     <br> tags (authors mark line breaks via <br>) and otherwise treat
     each child node / wrapped chunk as one line.
     ──────────────────────────────────────────────── */
  function wrapLines(el) {
    if (el.dataset.nzMaskReady) return;
    el.dataset.nzMaskReady = '1';

    // Build line list by splitting on <br> or by whitespace fallback.
    // Authors should write headings with <br> for explicit line breaks.
    const html = el.innerHTML;
    const parts = html.split(/<br\s*\/?>/i);
    const linesHTML = parts
      .map((s) => s.trim())
      .filter((s) => s.length)
      .map(
        (s) =>
          '<span class="nz-fancy-mask__line"><span class="nz-fancy-mask__inner">' +
          s +
          '</span></span>'
      )
      .join('');

    el.innerHTML = linesHTML;

    // Stagger inner span delays.
    const inners = el.querySelectorAll('.nz-fancy-mask__inner');
    inners.forEach((inner, i) => {
      inner.style.setProperty('--nz-mask-delay', i * 110 + 'ms');
    });
  }

  /* ────────────────────────────────────────────────
     2) Stagger children: assign stagger-delay var per child.
     ──────────────────────────────────────────────── */
  function applyStagger(parent) {
    if (parent.dataset.nzStaggerReady) return;
    parent.dataset.nzStaggerReady = '1';
    const step = parseInt(parent.dataset.nzStaggerStep || '90', 10);
    Array.from(parent.children).forEach((child, i) => {
      child.style.setProperty('--nz-stagger-delay', i * step + 'ms');
    });
  }

  /* ────────────────────────────────────────────────
     3) Reveal observer.
     ──────────────────────────────────────────────── */
  function setupReveals() {
    if (typeof IntersectionObserver === 'undefined') {
      // No IO support — just show everything.
      document
        .querySelectorAll('.nz-fancy-mask, [data-nz-fade], [data-nz-stagger], .nz-fancy-rule')
        .forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const headingEls = document.querySelectorAll('.nz-fancy-mask');
    headingEls.forEach(wrapLines);

    const stagEls = document.querySelectorAll('[data-nz-stagger]');
    stagEls.forEach(applyStagger);

    const targets = document.querySelectorAll(
      '.nz-fancy-mask, [data-nz-fade], [data-nz-stagger], .nz-fancy-rule'
    );

    // If reduced motion, just show everything immediately.
    if (reduceMotion) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-revealed');
          io.unobserve(e.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    targets.forEach((el) => io.observe(el));
  }

  /* ────────────────────────────────────────────────
     4) Parallax — single rAF loop applies a y translate based on each
        element's centre relative to viewport centre, scaled by factor.
     ──────────────────────────────────────────────── */
  function setupParallax() {
    if (reduceMotion) return;
    const els = Array.from(document.querySelectorAll('[data-nz-parallax]'));
    if (els.length === 0) return;

    let ticking = false;
    const vhMid = () => window.innerHeight / 2;

    const tick = () => {
      const mid = vhMid();
      els.forEach((el) => {
        const r = el.getBoundingClientRect();
        const center = r.top + r.height / 2;
        const delta = center - mid;
        const factor = parseFloat(el.dataset.nzParallax) || 0.12;
        const y = -delta * factor;
        el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0)`;
      });
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(tick);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    tick();
  }

  /* ────────────────────────────────────────────────
     5) Counter — animate from 0 to data-nz-counter value when in view.
     ──────────────────────────────────────────────── */
  function setupCounters() {
    const els = Array.from(document.querySelectorAll('[data-nz-counter]'));
    if (els.length === 0) return;

    const formatNum = (n, hasPlus, hasComma, decimals) => {
      let str;
      if (decimals > 0) str = n.toFixed(decimals);
      else str = Math.round(n).toString();
      if (hasComma) {
        const parts = str.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        str = parts.join('.');
      }
      return str + (hasPlus ? '+' : '');
    };

    const animate = (el) => {
      const raw = el.dataset.nzCounter || '0';
      const hasPlus = /\+$/.test(raw);
      const cleanRaw = raw.replace(/[+,]/g, '');
      const target = parseFloat(cleanRaw);
      const hasComma = raw.indexOf(',') > -1 || target >= 1000;
      const decimals = (cleanRaw.split('.')[1] || '').length;
      const dur = parseInt(el.dataset.nzCounterDur || '1500', 10);
      const start = performance.now();
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      const step = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const v = ease(t) * target;
        el.textContent = formatNum(v, hasPlus, hasComma, decimals);
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      els.forEach((el) => {
        el.textContent = el.dataset.nzCounter;
      });
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          animate(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.5 }
    );
    els.forEach((el) => {
      el.textContent = '0';
      io.observe(el);
    });
  }

  /* ────────────────────────────────────────────────
     Boot.
     ──────────────────────────────────────────────── */
  function init() {
    setupReveals();
    setupParallax();
    setupCounters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section editor reload events.
  document.addEventListener('shopify:section:load', init);
})();
